import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { Modal } from '../../../components';

import {
  deriveStopOptions,
  STOP_OUTCOME,
} from '../ManageListingCard/listingCardStopOptions';

import css from './StopListingModal.module.css';

export { STOP_OUTCOME };

/**
 * The three ways to take an advertisement down, and what each does to the money.
 *
 * The hardest thing for a seller to work out today is which of these they want. The
 * support complaint behind this dialog is a seller who marked their boat sold to stop
 * the direct debit — and one who closed the listing expecting the same, and kept
 * being charged. So each option states its consequence for the advertisement *and*
 * for the payment, side by side, rather than making anyone deduce it.
 */
const OPTIONS = [
  {
    key: STOP_OUTCOME.SOLD,
    tone: 'final',
    titleId: 'StopListingModal.sold.title',
    tagId: 'StopListingModal.sold.tag',
    adId: 'StopListingModal.sold.advertisement',
    payId: 'StopListingModal.sold.payment',
    confirmId: 'StopListingModal.sold.confirm',
    // The only irreversible choice, so it is the only one that asks the seller to
    // confirm they understand it.
    requiresAcknowledgement: true,
  },
  {
    key: STOP_OUTCOME.OFFLINE,
    tone: 'reversible',
    titleId: 'StopListingModal.offline.title',
    tagId: 'StopListingModal.offline.tag',
    adId: 'StopListingModal.offline.advertisement',
    payId: 'StopListingModal.offline.payment',
    confirmId: 'StopListingModal.offline.confirm',
  },
  {
    key: STOP_OUTCOME.CANCEL,
    tone: 'neutral',
    titleId: 'StopListingModal.cancel.title',
    tagId: 'StopListingModal.cancel.tag',
    adId: 'StopListingModal.cancel.advertisement',
    payId: 'StopListingModal.cancel.payment',
    confirmId: 'StopListingModal.cancel.confirm',
    // Cancelling a subscription they do not have makes no sense.
    privateSellerOnly: true,
  },
];

const TONE_CLASS = {
  final: css.optionFinal,
  reversible: css.optionReversible,
  neutral: css.optionNeutral,
};

const StopListingModal = props => {
  const {
    id,
    isOpen,
    onClose,
    onManageDisableScrolling,
    onConfirm,
    isBusinessSeller = false,
    inProgress = false,
    cardState,
    hasCancelScheduled = false,
  } = props;

  const intl = useIntl();
  const [choice, setChoice] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const availability = deriveStopOptions({
    state: cardState,
    hasCancelScheduled,
    isBusinessSeller,
  });
  const options = availability
    .map(a => ({ ...OPTIONS.find(o => o.key === a.key), ...a }))
    .filter(o => o.titleId);
  const selected = options.find(o => o.key === choice && o.isAvailable) || null;

  const needsAcknowledgement = !!selected?.requiresAcknowledgement;
  const canConfirm = !!selected && (!needsAcknowledgement || acknowledged) && !inProgress;

  const handleClose = () => {
    setChoice(null);
    setAcknowledged(false);
    onClose();
  };

  const handleChoose = key => {
    setChoice(key);
    // Ticking the box then changing your mind must not carry the acknowledgement over
    // to a different option.
    setAcknowledged(false);
  };

  return (
    <Modal
      id={id}
      containerClassName={css.root}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'Modal.close' })}
    >
      <h2 className={css.title}>
        <FormattedMessage id="StopListingModal.title" />
      </h2>
      <p className={css.intro}>
        <FormattedMessage
          id={isBusinessSeller ? 'StopListingModal.introBusiness' : 'StopListingModal.intro'}
        />
      </p>

      <div className={css.options} role="radiogroup" aria-label={intl.formatMessage({ id: 'StopListingModal.title' })}>
        {options.map(option => {
          const isSelected = choice === option.key;

          return (
            <div
              key={option.key}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={!option.isAvailable}
              tabIndex={option.isAvailable ? 0 : -1}
              className={classNames(css.option, TONE_CLASS[option.tone], {
                [css.optionSelected]: isSelected,
                [css.optionUnavailable]: !option.isAvailable,
              })}
              onClick={option.isAvailable ? () => handleChoose(option.key) : undefined}
              onKeyDown={e => {
                if (option.isAvailable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleChoose(option.key);
                }
              }}
            >
              <div className={css.optionHeader}>
                <span className={classNames(css.radio, { [css.radioChecked]: isSelected })} />
                <span className={css.optionTitle}>
                  <FormattedMessage id={option.titleId} />
                </span>
                <span className={css.tag}>
                  <FormattedMessage id={option.tagId} />
                </span>
              </div>

              {/* Two columns, because the whole point is seeing both consequences at
                  once. Business sellers have no payment column to show. */}
              <div
                className={classNames(css.consequences, {
                  [css.consequencesSingle]: isBusinessSeller,
                })}
              >
                <div className={css.consequence}>
                  <span className={css.consequenceHeading}>
                    <FormattedMessage id="StopListingModal.columnAdvertisement" />
                  </span>
                  <span className={css.consequenceValue}>
                    <FormattedMessage id={option.adId} />
                  </span>
                </div>
                {!isBusinessSeller && (
                  <div className={css.consequence}>
                    <span className={css.consequenceHeading}>
                      <FormattedMessage id="StopListingModal.columnPayment" />
                    </span>
                    <span className={classNames(css.consequenceValue, css.consequencePayment)}>
                      <FormattedMessage id={option.payId} />
                    </span>
                  </div>
                )}
              </div>

              {/* Says why, rather than leaving a greyed row to be puzzled over — and for
                  cancelling it also names where it can still be done. */}
              {!option.isAvailable && option.unavailableReasonId ? (
                <p className={css.unavailableReason}>
                  <FormattedMessage id={option.unavailableReasonId} />
                </p>
              ) : null}

              {isSelected && option.requiresAcknowledgement && (
                <label className={css.acknowledge} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className={css.acknowledgeInput}
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                  />
                  <span>
                    <FormattedMessage id="StopListingModal.acknowledge" />
                  </span>
                </label>
              )}
            </div>
          );
        })}
      </div>

      <div className={css.actions}>
        <button type="button" className={css.cancelButton} onClick={handleClose}>
          <FormattedMessage id="StopListingModal.cancelButton" />
        </button>
        <button
          type="button"
          className={classNames(css.confirmButton, {
            [css.confirmButtonFinal]: selected?.tone === 'final',
            [css.confirmButtonDisabled]: !canConfirm,
          })}
          disabled={!canConfirm}
          onClick={() => onConfirm(choice)}
        >
          {selected ? (
            <FormattedMessage id={selected.confirmId} />
          ) : (
            <FormattedMessage id="StopListingModal.chooseFirst" />
          )}
        </button>
      </div>
    </Modal>
  );
};

export default StopListingModal;
